// Opportunity (issue) create form. Title, short description, story,
// priority pills, term (short / long), anonymous toggle, home team,
// labels. The data model name is `issue` but every label says
// "opportunity".

import { useState } from "react";
import { Lightbulb, Plus, X } from "lucide-react";
import type { CreateSeed } from "./types";
import {
  FormSection,
  FormLabel,
  Hint,
  DC,
  twoCol,
} from "./primitives";
import { DcInput, TabChip, PrioritySelect, PillRemoveBtn } from "./fields";
import { RichTextEditor } from "../../components/RichTextEditor";
import { TeamSelect } from "./selects";
import { Footer } from "./Footer";
import { tpApi } from "../../api";
import { ISSUE_TERM, TODO_PRIORITY } from "../../../../../../../shared/team-planner/schemas";

interface OpportunityFormProps {
  seed?: CreateSeed;
  onClose: () => void;
  onCreated: () => void;
}

export function OpportunityForm({ seed, onClose, onCreated }: OpportunityFormProps) {
  const [title, setTitle] = useState(seed?.title ?? "");
  const [shortDescription, setShortDescription] = useState("");
  const [story, setStory] = useState("");
  const [priority, setPriority] = useState<
    (typeof TODO_PRIORITY)[number] | null
  >(null);
  const [term, setTerm] = useState<(typeof ISSUE_TERM)[number]>("short_term");
  const [anonymous, setAnonymous] = useState(false);
  const [homeTeam, setHomeTeam] = useState<string | null>(seed?.homeTeamId ?? null);
  const [labels, setLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const canSubmit = title.trim().length > 0 && !pending;
  const summary = anonymous
    ? "Will be raised anonymously"
    : homeTeam
      ? "Routed to a home team"
      : "Will appear on the open opportunities tab";

  async function submit() {
    setSubmitError(null);
    setPending(true);
    try {
      await tpApi.raiseIssue({
        title: title.trim(),
        shortDescription: shortDescription.trim() || null,
        story:
          story.trim() === "<p></p>" || story.trim() === ""
            ? null
            : story.trim(),
        priority: priority ?? undefined,
        term,
        isAnonymous: anonymous,
        homeTeamId: homeTeam ?? null,
        labels: labels.length > 0 ? labels : undefined,
      });
      onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <div style={bodyScrollStyle}>
        <FormSection>
          <FormLabel required>Opportunity title</FormLabel>
          <DcInput
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Standardise the onboarding playbook"
          />
        </FormSection>

        <FormSection>
          <FormLabel>Short description</FormLabel>
          <DcInput
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            maxLength={280}
            placeholder="One line — what's the spotter call?"
          />
          <Hint>Up to 280 characters — surfaced on the open tab card.</Hint>
        </FormSection>

        <FormSection>
          <FormLabel>Story</FormLabel>
          <RichTextEditor
            value={story}
            onChange={setStory}
            placeholder="What's the problem or idea? Why does it matter?"
            minRows={4}
          />
        </FormSection>

        <div style={twoCol}>
          <FormSection>
            <FormLabel>Priority</FormLabel>
            <PrioritySelect value={priority} onChange={setPriority} />
          </FormSection>
          <FormSection>
            <FormLabel>Term</FormLabel>
            <div style={{ display: "flex", gap: 6 }}>
              <TabChip
                active={term === "short_term"}
                onClick={() => setTerm("short_term")}
              >
                Short term
              </TabChip>
              <TabChip
                active={term === "long_term"}
                onClick={() => setTerm("long_term")}
              >
                Long term
              </TabChip>
            </div>
          </FormSection>
        </div>

        <FormSection>
          <FormLabel>Home team</FormLabel>
          <TeamSelect value={homeTeam} onChange={setHomeTeam} />
          <Hint>Optional — keep blank to land on the company-wide list.</Hint>
        </FormSection>

        <FormSection>
          <FormLabel>Privacy</FormLabel>
          <div style={{ display: "flex", gap: 6 }}>
            <TabChip
              active={!anonymous}
              onClick={() => setAnonymous(false)}
              icon={<Lightbulb size={13} />}
            >
              Public
            </TabChip>
            <TabChip
              active={anonymous}
              onClick={() => setAnonymous(true)}
              icon={<Lightbulb size={13} />}
            >
              Anonymous
            </TabChip>
          </div>
          {anonymous ? (
            <Hint>Hides your name on the card. Leaders can still see who raised it.</Hint>
          ) : null}
        </FormSection>

        <FormSection>
          <FormLabel>Labels</FormLabel>
          <div style={labelRow}>
            {labels.map((l, i) => (
              <span key={i} style={labelPill}>
                {l}
                <PillRemoveBtn onClick={() => setLabels((ls) => ls.filter((_, idx) => idx !== i))} />
              </span>
            ))}
            <input
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = labelInput.trim();
                  if (v) {
                    setLabels((ls) => [...ls, v]);
                    setLabelInput("");
                  }
                }
              }}
              placeholder={labels.length === 0 ? "Type and press Enter…" : "Add…"}
              style={{
                flex: 1,
                minWidth: 120,
                height: 24,
                border: 0,
                background: "transparent",
                outline: 0,
                font: "400 12px/1 var(--bsp-font-sans)",
                color: "var(--bsp-fg)",
              }}
            />
          </div>
        </FormSection>
      </div>
      <Footer
        color={DC.orange}
        primary="Raise opportunity"
        disabled={!canSubmit}
        onCancel={onClose}
        onSubmit={submit}
        error={submitError}
        summary={summary}
      />
    </>
  );
}

const bodyScrollStyle = {
  flex: 1,
  overflowY: "auto" as const,
  padding: "16px 22px 20px",
  display: "flex",
  flexDirection: "column" as const,
};

const labelRow = {
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "center",
  gap: 6,
  padding: "8px 10px",
  border: `1px solid ${DC.borderStrong}`,
  borderRadius: 6,
  background: DC.surface,
  minHeight: 38,
};

const labelPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 8px 4px 10px",
  background: DC.orangeTint,
  color: DC.orangeFg,
  borderRadius: 999,
  font: "600 11px/1 var(--bsp-font-sans)",
};
